import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, RefreshCw, Trash2, Pencil, FileText, Loader2 } from "lucide-react";
import { api, errMsg, guessTitle, LEVELS, SUBJECTS } from "@/lib/api";

const Slot = ({ subject, level, sample, onChanged, onEdit, onDelete }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("يرجى اختيار ملف PDF"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", guessTitle(file.name));
    fd.append("subject", subject.id);
    fd.append("level", level.n);
    fd.append("is_sample", true);
    fd.append("replace", Boolean(sample));
    try {
      await api.post("/admin/books", fd);
      toast.success(sample ? "تم استبدال المصدر" : "تم رفع المصدر");
      onChanged();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className={`p-4 border ${sample ? "bg-white border-[#E5DCCB]" : "border-dashed border-[#D6CBB5] bg-[#FAF7F2]"} flex flex-col gap-3 min-h-[150px]`} data-testid={`sample-slot-${subject.id}-${level.n}`}>
      <span className="text-xs font-bold text-[#B45309]">المستوى {level.n} · {level.name}</span>
      {sample ? (
        <>
          <p className="font-semibold text-sm flex items-start gap-2" dir="auto"><FileText className="w-4 h-4 mt-0.5 text-[#8B3A2B] shrink-0" /> {sample.title || sample.original_filename}</p>
          <p className="text-xs text-stone-600">{sample.page_count} صفحات · <span dir="ltr">{((sample.size ?? 0) / 1048576).toFixed(1)} MB</span></p>
          <div className="mt-auto flex gap-1">
            <button disabled={busy} onClick={() => ref.current?.click()} className="p-2 rounded-full hover:bg-[#F2ECE1]" aria-label="استبدال" title="استبدال" data-testid={`sample-slot-replace-${subject.id}-${level.n}`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}</button>
            <button onClick={() => onEdit(sample)} className="p-2 rounded-full hover:bg-[#F2ECE1]" aria-label="تعديل" title="تعديل" data-testid={`sample-slot-edit-${subject.id}-${level.n}`}><Pencil className="w-4 h-4" /></button>
            <button onClick={() => onDelete(sample)} className="p-2 rounded-full text-red-800 hover:bg-red-50" aria-label="حذف" title="حذف" data-testid={`sample-slot-delete-${subject.id}-${level.n}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        </>
      ) : (
        <button disabled={busy} onClick={() => ref.current?.click()} data-testid={`sample-slot-upload-${subject.id}-${level.n}`}
          className="mt-auto inline-flex items-center justify-center gap-2 h-10 rounded-full border border-[#8B3A2B] text-[#8B3A2B] text-sm font-semibold hover:bg-[#8B3A2B] hover:text-white transition-colors disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} رفع PDF
        </button>
      )}
      <input ref={ref} type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid={`sample-slot-input-${subject.id}-${level.n}`} />
    </div>
  );
};

export const SampleSlots = ({ samples, onChanged, onEdit, onDelete }) => (
  <div className="space-y-8" data-testid="admin-sample-slots">
    {SUBJECTS.map((s) => (
      <section key={s.id}>
        <h2 className="font-display text-2xl font-bold mb-3 flex items-center gap-3">
          <span className="w-3 h-3 rounded-full" style={{ background: s.tone }} /> {s.ar}
          <span className="text-sm font-normal text-stone-600">({samples.filter((x) => x.subject === s.id).length} من 4)</span>
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {LEVELS.map((l) => (
            <Slot key={l.n} subject={s} level={l} sample={samples.find((x) => x.subject === s.id && x.level === l.n)} onChanged={onChanged} onEdit={onEdit} onDelete={onDelete} />
          ))}
        </div>
      </section>
    ))}
  </div>
);
